import { graphqlRequest } from "@/graphql/client";
import { getContact, searchContact } from "@/graphql/queries";
import { IS_DEV } from "@/utils/env";

// SECURITY: Server-side fetch limit prevents DOS attacks through excessive page sizes
const SERVER_FETCH_LIMIT = 50; // Fetch 50 contacts at a time from server

// SECURITY: Safety limit to prevent infinite loops from malformed backend responses
const MAX_PAGES = 1000;

// SECURITY: Maximum length for search terms to prevent abuse
const MAX_SEARCH_TERM_LENGTH = 100;

/**
 * @param {string} dataString
 * @returns {{ contacts: unknown[], nextToken: object | null }}
 */
function parseContactsPayload(dataString) {
  let data;
  try {
    data = JSON.parse(dataString);
  } catch {
    return { contacts: [], nextToken: null };
  }
  if (data.items && Array.isArray(data.items)) {
    return { contacts: data.items, nextToken: data.next_token ?? null };
  }
  if (Array.isArray(data)) {
    return { contacts: data, nextToken: null };
  }
  return { contacts: data.contacts ?? [], nextToken: data.next_token ?? null };
}

/**
 * Decodes a base64-encoded UTF-8 string
 * @param {string} base64String - The base64 string to decode
 * @returns {string} The decoded UTF-8 string
 */
export const decodeBase64Utf8 = (s) => new TextDecoder().decode(Uint8Array.from(atob(s), (c) => c.charCodeAt(0)));

/** DEPRECATED: Use decodeBase64Utf8 instead */
export const decodeBase64Utf8Old = (base64String) => {
  try {
    const binaryString = atob(base64String);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    if (typeof TextDecoder !== "undefined") {
      return new TextDecoder("utf-8").decode(bytes);
    }
    // Fallback: best-effort decode
    let ascii = "";
    for (let i = 0; i < bytes.length; i++) ascii += String.fromCharCode(bytes[i]);
    try {
      return new TextDecoder().decode(Uint8Array.from(ascii, (c) => c.charCodeAt(0)));
    } catch (_) {
      return ascii;
    }
  } catch (e) {
    console.error("Failed to decode base64 utf-8", e);
    return "";
  }
};

/**
 * Fetches a single page of contacts from the API
 * SECURITY: Uses parameterized queries to prevent injection attacks
 * @param {string} customer - Customer identifier
 * @param {number} limit - Number of contacts to fetch per page
 * @param {object|null} token - Pagination token from previous response
 * @returns {Promise<{contacts: Array, nextToken: object|null}>} The contacts and next token
 */
export const fetchContactsPage = async (customer, limit = SERVER_FETCH_LIMIT, token = null) => {
  const params = {
    id: "",
    customer: customer,
    limit: limit,
    // AWSJSON expects a JSON string, not an object
    next_token: token ? JSON.stringify(token) : null,
  };

  try {
    const response = await graphqlRequest({
      query: getContact,
      variables: params,
      authMode: "AMAZON_COGNITO_USER_POOLS",
    });

    const body = response.data.getContact.body;
    const dataString = decodeBase64Utf8(body);
    if (IS_DEV) {
      console.log("[fetchContactsPage] dataString", dataString);
    }
    return parseContactsPayload(dataString);
  } catch (error) {
    console.error("Error fetching contacts:", error.message);
    throw new Error("Error fetching contacts: " + error.message);
  }
};

/**
 * Fetches all pages of contacts with pagination
 * ASSUMPTION: Backend enforces customer-level authorization on paginated results
 * SECURITY: Fetch all pages sequentially to enable full searchability while preventing duplicate fetches
 * RISK: Fetching all pages could consume significant memory if contact count is extremely large (10k+)
 * @param {string} customer - Customer identifier
 * @param {Function} onPageFetched - Optional callback called after each page is fetched with (pageContacts, allContactsSoFar)
 * @returns {Promise<Array>} All contacts across all pages
 */
export const fetchAllContactPages = async (customer, onPageFetched = null) => {
  let allContacts = [];
  let currentToken = null;
  let pageCount = 0;
  const seenTokens = new Set(); // SECURITY: Track tokens to detect and prevent infinite loops

  while (pageCount < MAX_PAGES) {
    const result = await fetchContactsPage(customer, SERVER_FETCH_LIMIT, currentToken);

    if (result?.contacts?.length) {
      allContacts = [...allContacts, ...result.contacts];

      // SECURITY: Call progress callback with current page data to enable progressive UI updates
      if (onPageFetched && typeof onPageFetched === "function") {
        try {
          onPageFetched(result.contacts, allContacts);
        } catch (error) {
          console.error("Error in onPageFetched callback:", error);
        }
      }

      // Check if there are more pages
      if (result.nextToken) {
        // SECURITY: Detect infinite loops by checking for duplicate tokens
        const tokenKey = JSON.stringify(result.nextToken);
        if (seenTokens.has(tokenKey)) {
          console.warn("Duplicate pagination token detected, stopping fetch to prevent infinite loop");
          break;
        }
        seenTokens.add(tokenKey);
        currentToken = result.nextToken;
        pageCount++;
      } else {
        // No more pages
        break;
      }
    } else {
      // No contacts in this page
      break;
    }
  }

  // SECURITY: Log if we hit the safety limit
  if (pageCount >= MAX_PAGES) {
    console.warn(`Reached maximum page limit (${MAX_PAGES}), stopping fetch`);
  }

  return allContacts;
};

/**
 * SECURITY: Sanitize search term to prevent injection attacks
 * @param {string} term - The search term to sanitize
 * @returns {string} The sanitized search term
 */
export const sanitizeSearchTerm = (term) => {
  if (!term || typeof term !== "string") return "";
  // SECURITY: Trim whitespace and limit length to prevent abuse
  const sanitized = term.trim();
  if (sanitized.length > MAX_SEARCH_TERM_LENGTH) {
    console.warn(`Search term truncated from ${sanitized.length} to ${MAX_SEARCH_TERM_LENGTH} characters`);
    return sanitized.substring(0, MAX_SEARCH_TERM_LENGTH);
  }
  return sanitized;
};

/**
 * Searches contacts with specified criteria
 * SECURITY: Uses parameterized queries to prevent injection attacks
 * RISK: Multiple search requests in quick succession could impact performance
 * @param {string} customer - Customer identifier
 * @param {object} searchParams - Search parameters (id, phone, email, first_name, last_name)
 * @param {number} limit - Number of contacts to fetch per page
 * @param {object|null} token - Pagination token from previous response
 * @returns {Promise<{contacts: Array, nextToken: object|null}>} The search results and next token
 */
export const searchContactsPage = async (customer, searchParams = {}, limit = SERVER_FETCH_LIMIT, token = null) => {
  // SECURITY: Validate and sanitize search parameters
  const sanitizedParams = {
    customer: customer,
    limit: limit,
    next_token: token ? JSON.stringify(token) : null,
  };

  // SECURITY: Only include search parameters that have values and sanitize them
  if (searchParams.id) {
    sanitizedParams.id = sanitizeSearchTerm(searchParams.id);
  }
  if (searchParams.phone) {
    sanitizedParams.phone = sanitizeSearchTerm(searchParams.phone);
  }
  if (searchParams.email) {
    sanitizedParams.email = sanitizeSearchTerm(searchParams.email);
  }
  if (searchParams.first_name) {
    sanitizedParams.first_name = sanitizeSearchTerm(searchParams.first_name);
  }
  if (searchParams.last_name) {
    sanitizedParams.last_name = sanitizeSearchTerm(searchParams.last_name);
  }

  try {
    const response = await graphqlRequest({
      query: searchContact,
      variables: sanitizedParams,
      authMode: "AMAZON_COGNITO_USER_POOLS",
    });

    const body = response?.data?.searchContact?.body;
    if (!body) {
      throw new Error("Empty searchContact response body");
    }
    const dataString = decodeBase64Utf8(body);
    if (IS_DEV) {
      console.log("[searchContactsPage] dataString:", dataString);
    }

    return parseContactsPayload(dataString);
  } catch (error) {
    const message =
      (error && typeof error === "object" && error.errors && error.errors[0] && error.errors[0].message) ||
      (error && typeof error === "object" && error.message) ||
      (typeof error === "string" ? error : null) ||
      "Unknown error";
    console.error("Error searching contacts:", message);
    throw new Error("Error searching contacts: " + message);
  }
};

/**
 * Fetches all pages of search results
 * ASSUMPTION: Backend enforces customer-level authorization on search results
 * @param {string} customer - Customer identifier
 * @param {object} searchParams - Search parameters (id, phone, email, first_name, last_name)
 * @param {Function} onPageFetched - Optional callback called after each page is fetched
 * @returns {Promise<Array>} All contacts matching search criteria
 */
export const searchAllContactPages = async (customer, searchParams = {}, onPageFetched = null) => {
  let allContacts = [];
  let currentToken = null;
  let pageCount = 0;
  const seenTokens = new Set(); // SECURITY: Track tokens to detect and prevent infinite loops

  while (pageCount < MAX_PAGES) {
    const result = await searchContactsPage(customer, searchParams, SERVER_FETCH_LIMIT, currentToken);

    if (result?.contacts?.length) {
      allContacts = [...allContacts, ...result.contacts];

      // SECURITY: Call progress callback with current page data
      if (onPageFetched && typeof onPageFetched === "function") {
        try {
          onPageFetched(result.contacts, allContacts);
        } catch (error) {
          console.error("Error in onPageFetched callback:", error);
        }
      }

      // Check if there are more pages
      if (result.nextToken) {
        // SECURITY: Detect infinite loops by checking for duplicate tokens
        const tokenKey = JSON.stringify(result.nextToken);
        if (seenTokens.has(tokenKey)) {
          console.warn("Duplicate pagination token detected in search, stopping to prevent infinite loop");
          break;
        }
        seenTokens.add(tokenKey);
        currentToken = result.nextToken;
        pageCount++;
      } else {
        break;
      }
    } else {
      break;
    }
  }

  // SECURITY: Log if we hit the safety limit
  if (pageCount >= MAX_PAGES) {
    console.warn(`Reached maximum page limit (${MAX_PAGES}) during search, stopping fetch`);
  }

  return allContacts;
};
